'use client';

import { useState } from 'react';
import { ProductEntry } from '../product/ProductEntry';
import { PageErrorBoundary } from '@/components/error/PageErrorBoundary';
import { Button } from '@/components/ui/button';
import type { ProductDto } from '@/core/application/dtos/ProductDto';
import type { ProductAggregateDocument } from '@/types/manage.types';
import { Input } from '../ui/input';

interface MenuPageClientProps {
  initialProducts: ProductDto[];
  initialCategories: string[];
  initialError: string | null;
}

const MenuPageClient = ({ initialProducts, initialCategories, initialError }: MenuPageClientProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  if (initialError) {
    console.error('Menu page error:', initialError);
  }

  // Convert ProductDto to ProductAggregateDocument for compatibility with ProductEntry
  const convertToAggregate = (products: ProductDto[]): ProductAggregateDocument[] => {
    return products.map((product) => ({
      id: product.id,
      name: product.name,
      description: product.description,
      category: product.category,
      images: product.images,
      slug: product.slug,
      ingredients: product.ingredients,
      attributes: product.attributes,
      allergen_info: {
        contains: product.allergenInfo.contains,
        may_contains: product.allergenInfo.mayContains,
      },
      availability: product.availability as
        | 'in_stock'
        | 'out_of_stock'
        | 'limited_stock'
        | 'pre_order'
        | 'discontinued',
      is_visible: product.isVisible,
      created_at: Date.now(),
      updated_at: Date.now(),
      variants: product.variants.map((variant) => ({
        id: variant.id,
        sku: variant.sku,
        product_id: variant.productId,
        attribute_combination: variant.attributeCombination.map((attr) => ({
          key: attr.key,
          value: attr.value,
          label: attr.label,
        })),
        price: variant.price,
        compare_at_price: variant.compareAtPrice,
        available: variant.availability as
          | 'in_stock'
          | 'out_of_stock'
          | 'limited_stock'
          | 'pre_order'
          | 'discontinued'
          | 'hidden',
        stock_count: variant.stockCount,
        images: variant.images,
        weight: variant.weight,
        dimensions: variant.dimensions,
        is_default: variant.isDefault,
        created_at: Date.now(),
        updated_at: Date.now(),
        on_stripe: variant.onStripe,
        stripe: {
          product_id: variant.stripeInfo?.productId,
          price_id: variant.stripeInfo?.priceId,
        },
      })),
    }));
  };

  // Filter products based on search and category
  const filteredProducts = convertToAggregate(initialProducts).filter((product) => {
    let matches = true;

    // Category filter
    if (selectedCategory) {
      matches = matches && product.category?.toLowerCase() === selectedCategory.toLowerCase();
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      matches =
        matches &&
        (product.name.toLowerCase().includes(query) ||
          product.description.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query));
    }

    return matches;
  });

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <PageErrorBoundary pageName="Menu">
      <div>
        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="max-w-md mx-auto">
            <Input
              type="text"
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2"
            />
          </div>

          {/* Category Filter */}
          {initialCategories.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                variant={selectedCategory === undefined ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(undefined)}
                className="rounded-full">
                All Items
              </Button>
              {initialCategories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category)}
                  className="rounded-full">
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Error State */}
        {initialError && (
          <div className="text-center py-12">
            <div className="space-y-4">
              <p className="text-red-600 font-medium">
                We&apos;re sorry, but we couldn&apos;t load the menu.
                <br /> Please try again later.
              </p>
              {/* <p className="text-gray-600 text-sm">{initialError}</p> */}
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!initialError && filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <span className="text-gray-500 text-2xl">🍽️</span>
              </div>
              <p className="text-gray-600 font-medium">No menu items found</p>
              <p className="text-gray-500 text-sm">
                {searchQuery || selectedCategory
                  ? 'Try adjusting your search or filter criteria'
                  : 'Menu items will appear here once they are available'}
              </p>
              {(searchQuery || selectedCategory) && (
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory(undefined);
                  }}
                  variant="outline">
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Menu Items Grid */}
        {!initialError && filteredProducts.length > 0 && (
          <div className="w-full text-sm pb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <ProductEntry product={product} key={product.id} />
            ))}
          </div>
        )}
      </div>
    </PageErrorBoundary>
  );
};

export default MenuPageClient;
